import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Modal";
import { ButtonShadcn } from "@/components/ui/Button";

import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

const DeleteFlowModal = ({ open, onModelClose, flow, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [inlineError, setInlineError] = useState("");

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setInlineError("");
    }
  }, [open]);

  const handleDelete = async () => {
    try {
      setLoading(true);
      setInlineError("");
      await onConfirm();
    } catch (e) {
      // The server names the specific reason a flow can't be deleted —
      // default flow, or still in use by one or more jobs.
      setInlineError(apiErrorMessageFormat(e, "Failed to delete flow"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onModelClose?.()}>
      <DialogContent className="w-[480px] md:w-full flex flex-col p-0">
        <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
          <div>
            <DialogTitle className="text-[15px] font-semibold text-text-primary">
              Delete Status Flow
            </DialogTitle>
            <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
              This removes the flow and every status it defines.
            </DialogDescription>
          </div>
          <DialogClose className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        <div className="space-y-4 p-5">
          <div className="rounded-2xl border border-border-primary bg-background-secondary p-4">
            <div className="flex gap-3">
              <div className="mt-0.5 text-destructive">
                <AlertTriangle size={18} />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold">{flow?.name || "this flow"}</span>?
                </p>
                <p className="mt-1 text-xs text-text-secondary">
                  A flow still selected by any job, or the tenant's Default
                  flow, cannot be deleted — this action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          {inlineError ? (
            <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">
              {inlineError}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border-primary bg-background-secondary">
          <ButtonShadcn
            type="button"
            variant="tertiary"
            size="lg"
            onClick={onModelClose}
            className="rounded-full px-5"
          >
            Cancel
          </ButtonShadcn>
          <ButtonShadcn
            type="button"
            variant="destructive"
            size="lg"
            disabled={loading}
            onClick={handleDelete}
            className="rounded-full px-5 border border-destructive/20"
          >
            {loading ? "Deleting..." : "Delete Flow"}
          </ButtonShadcn>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteFlowModal;
